import type { Maybe } from '@amazon/vinyl-util'
// Selection-source provenance parsed out of a CloudQueue edge's `metricId` — a
// FireFly metric-context JSON blob. Mirrors GH's read of the same field
// (MetricsStore.extractFireFlyPlaybackMetricsInfo → FireFlyMetricContext): the
// queue tells us which entity the customer selected and its source. Only the
// MTS attributes amu can't otherwise derive are lifted out; the rest of the
// blob is ignored. Pure: a corrupt / absent blob yields an empty result, and
// the sink supplies the wire defaults from there.

export interface SelectionSource {
    readonly selectionSourceId?: string
    readonly selectionSourceType?: string
    readonly isCustomerSelectedEntity?: boolean
    readonly fulfillmentSourceId?: string
    readonly fulfillmentSourceType?: string
}

/** The CloudQueue's own CSM / sub-CSM, which outrank the player's (GH precedence). */
export interface MetricIdSubscriptionModes {
    readonly metricIdContentSubscriptionMode?: string
    readonly metricIdSubContentSubscriptionMode?: string
}

/** The track-only FireFlyMetricContext fields GH stamps on the music streaming family. */
export interface TrackMetricContext extends MetricIdSubscriptionModes {
    readonly metricsSpec?: string
    readonly resourceType?: string
    readonly isPurchased?: boolean
    readonly isOnDemandQueue?: boolean
}

/** The podcast-only FireFlyMetricContext fields GH stamps on the podcast family. */
export interface PodcastMetricContext extends MetricIdSubscriptionModes {
    readonly playbackInstanceId?: string
    readonly podcastEpisodeId?: string
    readonly podcastShowId?: string
    readonly selectionSourceSessionId?: string
}

/** The FireFlyMetricContext fields GH's getCommonAdFields stamps on the ad family. */
export interface AdMetricContext extends MetricIdSubscriptionModes {
    readonly entityId?: string
    readonly entityIdType?: string
    readonly entityType?: string
    readonly playbackInstanceId?: string
    readonly playQueueId?: string
    readonly selectionSourceSubType?: string
    readonly selectionSourceSessionId?: string
    readonly metricsContextProvider?: string
    readonly usedCachedUrl?: boolean
    readonly resourceType?: string
}

function nonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined
}

// The metricId serializes its booleans as the strings "true" / "false".
function flag(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value
    if (value === 'true') return true
    if (value === 'false') return false
    return undefined
}

// GH's convertStringToSourceType: CloudQueue mislabels podcast sources as PODCAST.
function sourceType(value: unknown): string | undefined {
    const type = nonEmptyString(value)
    return type === 'PODCAST' ? 'PODCAST_SHOW' : type
}

function parseMetricIdBlob(metricId: Maybe<string>): Record<string, unknown> | null {
    if (!metricId) return null
    try {
        const parsed: unknown = JSON.parse(metricId)
        return parsed != null && typeof parsed === 'object'
            ? (parsed as Record<string, unknown>)
            : null
    } catch {
        return null
    }
}

type Defined<T> = { [K in keyof T]?: Exclude<T[K], undefined> }

function defined<T extends Record<string, unknown>>(fields: T): Defined<T> {
    return Object.fromEntries(
        Object.entries(fields).filter(([, value]) => value !== undefined)
    ) as Defined<T>
}

export function parseSelectionSource(metricId: Maybe<string>): SelectionSource {
    const blob = parseMetricIdBlob(metricId)
    if (!blob) return {}
    return defined({
        selectionSourceId: nonEmptyString(blob.selectionSourceId),
        selectionSourceType: sourceType(blob.selectionSourceType),
        isCustomerSelectedEntity: flag(blob.isCustomerSelectedEntity),
        fulfillmentSourceId: nonEmptyString(blob.fulfillmentSourceId),
        fulfillmentSourceType: sourceType(blob.fulfillmentSourceType),
    })
}

function subscriptionModes(blob: Record<string, unknown>) {
    return {
        metricIdContentSubscriptionMode: nonEmptyString(blob.contentSubscriptionMode),
        metricIdSubContentSubscriptionMode: nonEmptyString(blob.subContentSubscriptionMode),
    }
}

export function parseTrackMetricContext(metricId: Maybe<string>): TrackMetricContext {
    const blob = parseMetricIdBlob(metricId)
    if (!blob) return {}
    return defined({
        metricsSpec: nonEmptyString(blob.metricsSpec),
        resourceType: nonEmptyString(blob.resourceType),
        isPurchased: flag(blob.isPurchased),
        // Firefly does not report Unlimited stations as on demand although they are (GH forces it).
        isOnDemandQueue:
            blob.selectionSourceType === 'UNLIMITED_STATION' ? true : flag(blob.isOnDemandQueue),
        ...subscriptionModes(blob),
    })
}

export function parsePodcastMetricContext(metricId: Maybe<string>): PodcastMetricContext {
    const blob = parseMetricIdBlob(metricId)
    if (!blob) return {}
    return defined({
        playbackInstanceId: nonEmptyString(blob.playbackInstanceId),
        podcastEpisodeId: nonEmptyString(blob.podcastEpisodeId),
        podcastShowId: nonEmptyString(blob.podcastShowId),
        selectionSourceSessionId: nonEmptyString(blob.selectionSourceSessionId),
        ...subscriptionModes(blob),
    })
}

export function parseAdMetricContext(metricId: Maybe<string>): AdMetricContext {
    const blob = parseMetricIdBlob(metricId)
    if (!blob) return {}
    return defined({
        entityId: nonEmptyString(blob.entityId),
        entityIdType: nonEmptyString(blob.entityIdType),
        entityType: nonEmptyString(blob.entityType),
        playbackInstanceId: nonEmptyString(blob.playbackInstanceId),
        playQueueId: nonEmptyString(blob.playQueueId),
        selectionSourceSubType: nonEmptyString(blob.selectionSourceSubType),
        selectionSourceSessionId: nonEmptyString(blob.selectionSourceSessionId),
        metricsContextProvider: nonEmptyString(blob.metricsContextProvider),
        usedCachedUrl: flag(blob.usedCachedUrl),
        resourceType: nonEmptyString(blob.resourceType),
        ...subscriptionModes(blob),
    })
}

/** GH's startPreviewPlayback context: a preview has no queue, so its metricId is seeded here. */
export function previewMetricId(entityId: string): string {
    return JSON.stringify({
        metricsSpec: 'TRACK_PREVIEW',
        selectionSourceId: entityId,
        selectionSourceType: 'SONGS',
        fulfillmentSourceId: entityId,
        fulfillmentSourceType: 'SONGS',
        resourceType: 'CLOUD_PLAYER',
        isCustomerSelectedEntity: true,
    })
}
